import { useCallback, useMemo, useState } from 'react';
import { useEditContext } from 'react-admin';
import { useDropzone } from 'react-dropzone';
import { useController } from 'react-hook-form';

import CloudUpload from '@mui/icons-material/CloudUploadOutlined';
import { Button, CircularProgress } from '@mui/material';

import { GenericObject } from '../Table/DefaultTable/types';

export enum ASPECT_RATIO_TYPES {
  LOGO = 'LOGO',
  THUMBNAIL = 'THUMBNAIL',
  ALL = 'ALL',
}

interface ImageSelectorProps {
  image: File | null | undefined;
  aspectRatio?: ASPECT_RATIO_TYPES;
  nonRequired?: boolean;
  label?: string;
  handleChangeImage(logo: File): void;
  getImageId(record: GenericObject): string;
  getImagePath(record: GenericObject): string;
  isMedia?: boolean;
}

type ImageSizeProps = {
  width: number;
  height: number;
};

const imageTypes = {
  JPEG: 'image/jpeg',
  PNG: 'image/png',
};

const validTypes = [imageTypes.JPEG, imageTypes.PNG];

const validExtensions = ['png', 'jpg', 'jpeg'];

const MAX_FILE_SIZE_IN_MB = 2;

export const ImageSelector = ({
  image,
  handleChangeImage,
  label = 'Photo',
  getImageId,
  getImagePath,
  isMedia,
  nonRequired = false,
  aspectRatio = ASPECT_RATIO_TYPES.LOGO,
}: ImageSelectorProps) => {
  const { record } = useEditContext();
  const [imageValidationError, setImageValidationError] = useState<string | null>();
  const {
    fieldState: { error },
  } = useController({ name: 'logo', defaultValue: null });

  const getAspectRatioLabel = () => {
    const thumbnail = '16:9';
    const logo = '1:1';
    if (ASPECT_RATIO_TYPES.THUMBNAIL === aspectRatio) {
      return thumbnail;
    } else if (ASPECT_RATIO_TYPES.ALL === aspectRatio) {
      return `${thumbnail} or ${logo}`;
    } else {
      return logo;
    }
  };

  const validAspectRatio = async (file: File) => {
    const isValid = await isAspectRatioValid(file);
    if (isValid) {
      setImageValidationError(null);
      return true;
    } else {
      setImageValidationError(`Aspect ratio must be ${getAspectRatioLabel()}`);
      return false;
    }
  };

  const validFileSize = (file: File) => {
    // Get length of file in bytes
    const fileSizeInBytes = file.size;
    // Convert the bytes to Kilobytes (1 KB = 1024 Bytes)
    const fileSizeInKB = fileSizeInBytes / 1024;
    // Convert the KB to MegaBytes (1 MB = 1024 KBytes)
    const fileSizeInMB = fileSizeInKB / 1024;

    if (fileSizeInMB > MAX_FILE_SIZE_IN_MB) {
      setImageValidationError(`File size must be lower than ${MAX_FILE_SIZE_IN_MB}mb`);
      return false;
    } else {
      setImageValidationError(null);
      return true;
    }
  };

  const getFileExtension = (file: File) => {
    if (!file.name) {
      return '';
    }

    const fileName = file.name;
    const fileNameExtensionSplit = fileName.split('.');
    const extension = fileNameExtensionSplit[fileNameExtensionSplit.length - 1];

    return extension ?? '';
  };

  const validFileExtension = (file: File) => {
    if (!validTypes.includes(file.type) || !validExtensions.includes(getFileExtension(file))) {
      setImageValidationError('File should be JPG or PNG');
      return false;
    } else {
      setImageValidationError(null);
      return true;
    }
  };

  const validateImage = async (file: File) => {
    const fileExtensionValid = validFileExtension(file);

    if (!fileExtensionValid) {
      return;
    }

    const fileSizeValid = validFileSize(file);

    if (!fileSizeValid) {
      return;
    }

    const aspectRatioValid = await validAspectRatio(file);

    if (!aspectRatioValid) {
      return;
    }

    handleChangeImage(file);
  };

  const validator = (file: File) => {
    validateImage(file);

    return {
      code: 'default',
      message: 'default',
    };
  };

  const [isImageLoading, setIsImageLoading] = useState(true);
  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    maxSize: MAX_FILE_SIZE_IN_MB,
    accept: {
      [imageTypes.JPEG]: ['.jpeg', '.jpg'],
      [imageTypes.PNG]: ['.png'],
    },
    validator,
  });
  const inputProps = { ...getInputProps() };

  const getFile = () => {
    if (acceptedFiles && acceptedFiles?.length && acceptedFiles[0]) {
      return URL.createObjectURL(acceptedFiles[0]);
    } else if (image) {
      return URL.createObjectURL(image);
    } else if (getImageId(record) || isMedia) {
      return `${getImagePath(record)}`;
    }

    return '/default.png';
  };

  const handleImageOnLoad = () => {
    setIsImageLoading(false);
  };

  const getImageSize = useCallback((url: string) => {
    const img = document.createElement('img');

    const promise = new Promise((resolve, reject) => {
      img.onload = () => {
        // Natural size is the actual image size regardless of rendering.
        // The 'normal' `width`/`height` are for the **rendered** size.
        const width = img.naturalWidth;
        const height = img.naturalHeight;

        // Resolve promise with the width and height
        resolve({ width, height });
      };

      // Reject promise on error
      img.onerror = reject;
    });

    // Setting the source makes it start downloading and eventually call `onload`
    img.src = url;

    return promise;
  }, []);

  const isAspectRatioValid = useCallback(
    async (file: File) => {
      var objectUrl = URL.createObjectURL(file);
      const imageSize: ImageSizeProps = (await getImageSize(objectUrl)) as ImageSizeProps;

      const imageAspectRatio = imageSize?.width / imageSize?.height;

      const isThumbnail = imageAspectRatio > 1.75 && imageAspectRatio < 1.8;
      const isLogo = imageAspectRatio === 1;

      if (aspectRatio === ASPECT_RATIO_TYPES.THUMBNAIL) {
        return isThumbnail;
      } else if (aspectRatio === ASPECT_RATIO_TYPES.ALL) {
        return isThumbnail || isLogo;
      } else {
        return isLogo;
      }
    },
    [getImageSize, aspectRatio]
  );

  const isFileMissing = useMemo(() => {
    return !image && error;
  }, [image, error]);

  const hasError = useMemo(() => isFileMissing || imageValidationError, [isFileMissing, imageValidationError]);

  return (
    <div>
      <div style={{ display: 'flex', height: 121, padding: 15 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ textAlign: 'center', color: hasError ? '#d32f2f' : '' }}>
            {label} {!nonRequired && '*'}
          </div>
          <div style={{ display: isImageLoading ? 'block' : 'none', marginTop: 10 }}>
            <CircularProgress size={55} />
          </div>
          <img
            style={{ display: isImageLoading ? 'none' : 'block', maxWidth: 80, maxHeight: 60 }}
            onLoad={handleImageOnLoad}
            src={getFile()}
            alt="Logo"
          />
        </div>
        <div
          style={{
            border: hasError ? '2px dashed rgba(255, 0, 0, 0.25)' : '2px dashed rgba(0, 0, 0, 0.25)',
            borderRadius: 10,
            display: 'flex',
            marginLeft: 20,
            width: '100%',
            cursor: 'pointer',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
          }}
          {...getRootProps({ className: 'dropzone' })}>
          <input {...inputProps} />
          <div>
            <CloudUpload sx={{ fontSize: 50 }} />
          </div>
          <div style={{ marginLeft: 40, marginRight: 40 }}>
            <p style={{ color: '#000', fontSize: 13, textAlign: 'center' }}>Select a file or drag and drop here</p>
            <p style={{ color: 'rgba(0, 0, 0, 0.4)', fontSize: 10 }}>
              JPG or PNG | Maximum: {MAX_FILE_SIZE_IN_MB}MB | Aspect ratio: {getAspectRatioLabel()}
            </p>
          </div>
          <Button size="small" variant="outlined">
            SELECT FILE
          </Button>
        </div>
      </div>
      {hasError && (
        <p data-cy="image-error" style={{ color: '#d32f2f', fontSize: 10, textAlign: 'center', marginTop: '-10px' }}>
          {imageValidationError ? imageValidationError : isFileMissing ? error?.message : ''}
        </p>
      )}
    </div>
  );
};
